package co.uk.paxes.pinpoint.web.ads

import co.uk.paxes.pinpoint.web.platform.PlatformDetector
import kotlinx.browser.window
import kotlin.js.JsExport
import kotlin.js.JsName
import kotlin.js.Promise

/**
 * Central service for Facebook Instant Games ad management.
 * 
 * Handles ad loading, display, preloading, and lifecycle management.
 * Only operates when running on Facebook platform.
 */
@JsExport
object FacebookAdService {
    
    /**
     * Ad loading states for tracking ad lifecycle.
     */
    enum class AdLoadingState {
        NOT_LOADED,
        LOADING,
        LOADED,
        SHOWING,
        ERROR
    }
    
    /**
     * Ad statistics for debugging and monitoring.
     */
    data class AdStatistics(
        val successCount: Int,
        val failureCount: Int,
        val failureRate: Double
    )
    
    // Configuration
    private const val MAX_RETRY_ATTEMPTS = 3
    private const val RETRY_DELAY_MS = 5000
    
    // State tracking
    private val adStates: MutableMap<String, AdLoadingState> = mutableMapOf()
    private val preloadedAds: MutableMap<String, dynamic> = mutableMapOf()
    private val retryAttempts: MutableMap<String, Int> = mutableMapOf()
    
    // Statistics
    private var adLoadSuccessCount: Int = 0
    private var adLoadFailureCount: Int = 0
    
    // Initialization flag
    private var isInitialized: Boolean = false
    
    /**
     * Initialize the ad service and preload common ads.
     * Only initializes if platform is Facebook.
     */
    @JsName("initialize")
    fun initialize() {
        if (isInitialized) {
            console.log("⚠️ FacebookAdService: Already initialized")
            return
        }
        
        if (!PlatformDetector.isFacebook()) {
            console.log("ℹ️ FacebookAdService: Skipping initialization (standalone mode)")
            return
        }
        
        console.log("🎬 FacebookAdService: Initializing...")
        
        // Preload common ads
        preloadAd("unlock_hard", isRewarded = true)
        preloadAd("unlock_expert", isRewarded = true)
        preloadAd("undo_credit", isRewarded = true)
        
        isInitialized = true
        console.log("✅ FacebookAdService: Initialized successfully")
    }
    
    /**
     * Show a rewarded video ad.
     * 
     * @param placementID The ad placement identifier
     * @param onCompleted Callback when ad completes successfully
     * @param onDismissed Callback when ad is dismissed early
     * @param onFailed Callback when ad fails to load/show
     */
    @JsName("showRewardedAd")
    fun showRewardedAd(
        placementID: String,
        onCompleted: () -> Unit,
        onDismissed: () -> Unit,
        onFailed: () -> Unit
    ) {
        console.log("📺 FacebookAdService: Showing rewarded ad for placement: $placementID")
        
        if (!PlatformDetector.isFacebook()) {
            console.log("⚠️ FacebookAdService: Not on Facebook platform, calling onFailed")
            onFailed()
            return
        }
        
        // Show loading indicator
        showLoadingIndicator()
        
        // Check if ad is preloaded
        val preloadedAd = preloadedAds[placementID]
        if (preloadedAd != null) {
            console.log("✅ FacebookAdService: Using preloaded ad")
            showPreloadedAd(preloadedAd, placementID, onCompleted, onDismissed, onFailed)
        } else {
            console.log("⏳ FacebookAdService: Loading ad...")
            setState(placementID, AdLoadingState.LOADING)
            loadAndShowRewardedAd(placementID, onCompleted, onDismissed, onFailed)
        }
    }
    
    /**
     * Show an interstitial ad.
     * 
     * @param placementID The ad placement identifier
     * @param onClosed Callback when ad is closed
     * @param onFailed Callback when ad fails
     */
    @JsName("showInterstitialAd")
    fun showInterstitialAd(
        placementID: String,
        onClosed: () -> Unit,
        onFailed: () -> Unit
    ) {
        console.log("📺 FacebookAdService: Showing interstitial ad for placement: $placementID")
        
        if (!PlatformDetector.isFacebook()) {
            console.log("⚠️ FacebookAdService: Not on Facebook platform, calling onFailed")
            onFailed()
            return
        }
        
        setState(placementID, AdLoadingState.LOADING)
        loadAndShowInterstitialAd(placementID, onClosed, onFailed)
    }
    
    /**
     * Get current ad loading state.
     */
    @JsName("getAdState")
    fun getAdState(placementID: String): AdLoadingState {
        return adStates[placementID] ?: AdLoadingState.NOT_LOADED
    }
    
    /**
     * Get ad statistics for debugging.
     */
    @JsName("getStatistics")
    fun getStatistics(): AdStatistics {
        val total = adLoadSuccessCount + adLoadFailureCount
        val failureRate = if (total > 0) adLoadFailureCount.toDouble() / total else 0.0
        return AdStatistics(adLoadSuccessCount, adLoadFailureCount, failureRate)
    }
    
    /**
     * Preload an ad for later use.
     */
    private fun preloadAd(placementID: String, isRewarded: Boolean) {
        console.log("🔄 FacebookAdService: Preloading ad: $placementID")
        
        setState(placementID, AdLoadingState.LOADING)
        
        val loadPromise: Promise<dynamic> = if (isRewarded) {
            js("FBInstant.getRewardedVideoAsync(placementID)") as Promise<dynamic>
        } else {
            js("FBInstant.getInterstitialAdAsync(placementID)") as Promise<dynamic>
        }
        
        loadPromise.then({ adInstance: dynamic ->
            (adInstance.loadAsync() as Promise<Unit>).then({
                preloadedAds[placementID] = adInstance
                setState(placementID, AdLoadingState.LOADED)
                adLoadSuccessCount++
                retryAttempts.remove(placementID)
                console.log("✅ FacebookAdService: Preloaded ad: $placementID")
            }, { error: dynamic ->
                handlePreloadFailure(placementID, isRewarded, error)
            })
        }, { error: dynamic ->
            handlePreloadFailure(placementID, isRewarded, error)
        })
    }
    
    /**
     * Handle preload failure with retry logic.
     */
    private fun handlePreloadFailure(placementID: String, isRewarded: Boolean, error: dynamic) {
        setState(placementID, AdLoadingState.ERROR)
        adLoadFailureCount++
        
        val attempts = retryAttempts[placementID] ?: 0
        console.error("❌ FacebookAdService: Preload failed for $placementID (attempt ${attempts + 1}/$MAX_RETRY_ATTEMPTS): ${error.message}")
        
        if (attempts < MAX_RETRY_ATTEMPTS) {
            retryAttempts[placementID] = attempts + 1
            console.log("🔄 FacebookAdService: Retrying preload in ${RETRY_DELAY_MS}ms...")
            window.setTimeout({
                preloadAd(placementID, isRewarded)
            }, RETRY_DELAY_MS)
        } else {
            console.error("❌ FacebookAdService: Max retry attempts reached for $placementID")
            retryAttempts.remove(placementID)
        }
    }
    
    /**
     * Load and show a rewarded ad.
     */
    private fun loadAndShowRewardedAd(
        placementID: String,
        onCompleted: () -> Unit,
        onDismissed: () -> Unit,
        onFailed: () -> Unit
    ) {
        val loadPromise = js("FBInstant.getRewardedVideoAsync(placementID)") as Promise<dynamic>
        
        loadPromise.then({ adInstance: dynamic ->
            (adInstance.loadAsync() as Promise<Unit>).then({
                hideLoadingIndicator()
                setState(placementID, AdLoadingState.LOADED)
                adLoadSuccessCount++
                console.log("✅ FacebookAdService: Ad loaded, showing...")
                showPreloadedAd(adInstance, placementID, onCompleted, onDismissed, onFailed)
            }, { error: dynamic ->
                hideLoadingIndicator()
                setState(placementID, AdLoadingState.ERROR)
                adLoadFailureCount++
                console.error("❌ FacebookAdService: Ad load failed: ${error.message}")
                onFailed()
            })
        }, { error: dynamic ->
            hideLoadingIndicator()
            setState(placementID, AdLoadingState.ERROR)
            adLoadFailureCount++
            console.error("❌ FacebookAdService: Ad instance creation failed: ${error.message}")
            onFailed()
        })
    }
    
    /**
     * Show a preloaded ad.
     */
    private fun showPreloadedAd(
        adInstance: dynamic,
        placementID: String,
        onCompleted: () -> Unit,
        onDismissed: () -> Unit,
        onFailed: () -> Unit
    ) {
        setState(placementID, AdLoadingState.SHOWING)
        hideLoadingIndicator()
        
        (adInstance.showAsync() as Promise<Unit>).then({
            console.log("✅ FacebookAdService: Ad completed for $placementID")
            setState(placementID, AdLoadingState.NOT_LOADED)
            preloadedAds.remove(placementID)
            
            // Grant reward before callback
            onCompleted()
            
            // Preload next ad
            preloadAd(placementID, isRewarded = true)
        }, { error: dynamic ->
            console.error("❌ FacebookAdService: Ad show failed: ${error.message}")
            setState(placementID, AdLoadingState.ERROR)
            preloadedAds.remove(placementID)
            
            // Check if dismissed or failed
            val errorCode = error.code as? String
            if (errorCode == "USER_INPUT") {
                console.log("ℹ️ FacebookAdService: Ad dismissed by user")
                onDismissed()
            } else {
                onFailed()
            }
        })
    }
    
    /**
     * Load and show an interstitial ad.
     */
    private fun loadAndShowInterstitialAd(
        placementID: String,
        onClosed: () -> Unit,
        onFailed: () -> Unit
    ) {
        val loadPromise = js("FBInstant.getInterstitialAdAsync(placementID)") as Promise<dynamic>
        
        loadPromise.then({ adInstance: dynamic ->
            (adInstance.loadAsync() as Promise<Unit>).then({
                setState(placementID, AdLoadingState.LOADED)
                adLoadSuccessCount++
                console.log("✅ FacebookAdService: Interstitial loaded, showing...")
                
                setState(placementID, AdLoadingState.SHOWING)
                (adInstance.showAsync() as Promise<Unit>).then({
                    console.log("✅ FacebookAdService: Interstitial completed")
                    setState(placementID, AdLoadingState.NOT_LOADED)
                    onClosed()
                }, { error: dynamic ->
                    console.error("❌ FacebookAdService: Interstitial show failed: ${error.message}")
                    setState(placementID, AdLoadingState.ERROR)
                    onFailed()
                })
            }, { error: dynamic ->
                setState(placementID, AdLoadingState.ERROR)
                adLoadFailureCount++
                console.error("❌ FacebookAdService: Interstitial load failed: ${error.message}")
                onFailed()
            })
        }, { error: dynamic ->
            setState(placementID, AdLoadingState.ERROR)
            adLoadFailureCount++
            console.error("❌ FacebookAdService: Interstitial instance creation failed: ${error.message}")
            onFailed()
        })
    }
    
    /**
     * Set ad state and log transition.
     */
    private fun setState(placementID: String, newState: AdLoadingState) {
        val oldState = adStates[placementID] ?: AdLoadingState.NOT_LOADED
        adStates[placementID] = newState
        console.log("🔄 FacebookAdService: State transition for $placementID: $oldState → $newState")
    }
    
    /**
     * Show loading indicator.
     */
    private fun showLoadingIndicator() {
        // TODO: Implement UI loading indicator
        console.log("⏳ Showing loading indicator...")
    }
    
    /**
     * Hide loading indicator.
     */
    private fun hideLoadingIndicator() {
        // TODO: Implement UI loading indicator
        console.log("✅ Hiding loading indicator...")
    }
}
